import React from "react";
import { CalendarRange } from "lucide-react";
import { Input } from "../../ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";

const MODES = [
  { id: "month", label: "Month" },
  { id: "vat_bimonthly", label: "VAT period" },
  { id: "year", label: "Full year" },
  { id: "ytd", label: "Year to date" },
  { id: "custom", label: "Custom" },
];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const VAT_PERIODS = [
  { start: 1, label: "Jan-Feb" }, { start: 3, label: "Mar-Apr" },
  { start: 5, label: "May-Jun" }, { start: 7, label: "Jul-Aug" },
  { start: 9, label: "Sep-Oct" }, { start: 11, label: "Nov-Dec" },
];
const pad = (n) => String(n).padStart(2, "0");

export const PeriodFilter = ({ value, bounds, rangeLabel, onChange }) => {
  const today = bounds?.today || new Date().toISOString().slice(0, 10);
  const minYear = bounds?.min_year || 2024;
  const maxYear = bounds?.max_year || Number(today.slice(0, 4)) + 1;
  const years = [];
  for (let y = maxYear; y >= minYear; y -= 1) years.push(y);

  const anchor = value.from || today.slice(0, 7);
  const year = Number(String(anchor).slice(0, 4)) || Number(today.slice(0, 4));
  const monthNo = Number(String(anchor).slice(5, 7)) || Number(today.slice(5, 7));
  const vatStart = monthNo % 2 === 0 ? monthNo - 1 : monthNo;

  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="finance-period-filter">
      <Select value={value.period} onValueChange={(period) => {
        if (period === "month") set({ period, from: `${year}-${pad(monthNo)}`, to: null });
        else if (period === "vat_bimonthly") set({ period, from: `${year}-${pad(vatStart)}`, to: null });
        else if (period === "year" || period === "ytd") set({ period, from: String(year), to: null });
        else set({ period, from: `${year}-${pad(monthNo)}-01`, to: today });
      }}>
        <SelectTrigger className="h-9 w-[150px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                       data-testid="finance-period-mode">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MODES.map((m) => (
            <SelectItem key={m.id} value={m.id} data-testid={`finance-mode-${m.id}`}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {value.period === "month" && (
        <Select value={pad(monthNo)}
                onValueChange={(m) => set({ from: `${year}-${m}` })}>
          <SelectTrigger className="h-9 w-[110px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                         data-testid="finance-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTHS.map((label, i) => (
              <SelectItem key={label} value={pad(i + 1)}
                          data-testid={`finance-month-opt-${pad(i + 1)}`}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {value.period === "vat_bimonthly" && (
        <Select value={pad(vatStart)}
                onValueChange={(m) => set({ from: `${year}-${m}` })}>
          <SelectTrigger className="h-9 w-[130px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                         data-testid="finance-vat-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {VAT_PERIODS.map((p) => (
              <SelectItem key={p.start} value={pad(p.start)}
                          data-testid={`finance-vat-opt-${pad(p.start)}`}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {value.period !== "custom" && (
        <Select value={String(year)} onValueChange={(y) => {
          if (value.period === "year" || value.period === "ytd") set({ from: y });
          else if (value.period === "vat_bimonthly") set({ from: `${y}-${pad(vatStart)}` });
          else set({ from: `${y}-${pad(monthNo)}` });
        }}>
          <SelectTrigger className="h-9 w-[100px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                         data-testid="finance-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}
                          data-testid={`finance-year-opt-${y}`}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {value.period === "custom" && (
        <>
          <Input type="date" value={value.from || ""} min={bounds?.min_date}
                 max={bounds?.max_date}
                 onChange={(e) => set({ from: e.target.value })}
                 className="h-9 w-[150px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                 data-testid="finance-custom-from" />
          <span className="text-slate-500 text-xs">to</span>
          <Input type="date" value={value.to || ""} min={value.from || bounds?.min_date}
                 max={bounds?.max_date}
                 onChange={(e) => set({ to: e.target.value })}
                 className="h-9 w-[150px] bg-slate-800 border-slate-700 text-slate-200 text-xs"
                 data-testid="finance-custom-to" />
        </>
      )}

      {rangeLabel && (
        <span className="inline-flex items-center gap-1.5 text-xs text-saturn-orange bg-saturn-orange/10 border border-saturn-orange/30 rounded-full px-3 py-1.5"
              data-testid="finance-range-label">
          <CalendarRange className="w-3 h-3" /> {rangeLabel}
        </span>
      )}
    </div>
  );
};

export default PeriodFilter;
